import { Typography, Grid } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import {
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  required,
  EditProps,
  SelectInput,
  TextInput,
  FormDataConsumer,
  useEditController,
} from "react-admin";
import { GetParam } from "utils/tools";
import React from "react";
import CustomEditToolbar from "../../components/CustomEditToolbar";
import CustomNumberInput from "components/CustomNumberInput";
import { vehicleSize } from "../routeplans/const";
import CustomDatePickerInput from "../../components/CustomDatePickerInput";
import EditWrapper from "../../components/EditWrapper";
import NewSubcontractorInput from "./components/NewSubcontractorInput";
import PermissionLoading from "../../components/PermissionLoading";
import SubcontractorTimesheetAdhocFeeTable from "./components/SubcontractorTimesheetAdhocFeeTable";
import { isEmpty } from "lodash";

// This is the validation function passed to <SimpleForm validate={formValidate} />
const formValidate = (values: any) => {
  const errors: any = {};

  // Validate `totalStudent` field
  // It is required only when `isUpdateSubcontractor` is false or not set
  if (!values.isUpdateSubcontractor && !values.totalStudent) {
    errors.totalStudent = "Required";
  }

  // Validate `vehicleSize` field
  // It is required only when `isUpdateSubcontractor` is false or not set
  if (!values.isUpdateSubcontractor && !values.vehicleSize) {
    errors.vehicleSize = "Required";
  }

  // Validate `newSubcontractorId` field
  // It is required only when `isUpdateSubcontractor` is true
  if (values.isUpdateSubcontractor && !values.newSubcontractorId) {
    errors.newSubcontractorId = "Required";
  }

  if (
    values.isUpdateSubcontractor &&
    values.newSubcontractorId &&
    values.subcontractorId &&
    String(values.newSubcontractorId) === String(values.subcontractorId)
  ) {
    errors.newSubcontractorId =
      "Cannot be the same as the current subcontractor";
  }

  // If this object is empty, the form is considered valid
  return errors;
};

const SubcontractorTimesheetEdit = (props: EditProps) => {
  const { id } = props;
  const { record, loading } = useEditController(props);
  const param = GetParam();
  const isSpecificSubcontractor = !!param.get("subcontractorId");

  const adhocSubcontractorId = Number(param.get("adhocSubcontractorId"));

  let breadcrumbValue;
  let redirectAction;

  if (isSpecificSubcontractor) {
    breadcrumbValue = [
      {
        title: "Subcontractors",
        link: `/subcontractors/${param.get("subcontractorId")}/show`,
      },
      {
        title: "Timesheet",
        link: `/subcontractors/${param.get("subcontractorId")}/show/3`,
      },
      {
        title: id,
        link: "",
      },
    ];
    redirectAction = `/subcontractors/${param.get("subcontractorId")}/show/3`;
  } else {
    breadcrumbValue = [
      {
        title: "Subcontractor Timesheets",
        link: `/subcontractorTimesheets`,
      },
      {
        title: id,
        link: "",
      },
    ];
    redirectAction = `/subcontractorTimesheets`;
  }

  const breadcrumb = breadcrumbValue;

  const formatRecord = (res: any) => {
    if (res) {
      const hasOldSubcontractorAdhocFee =
        res.oldSubcontractorAdhocFee && !isEmpty(res.oldSubcontractorAdhocFee);
      const hasNewSubcontractorAdhocFee =
        res.newSubcontractorAdhocFee && !isEmpty(res.newSubcontractorAdhocFee);

      res.lastSavedTotalStudent = res.totalStudent;
      res.lastSavedVehicleSize = res.vehicleSize;
      res.hasExistingAdhocFees =
        hasOldSubcontractorAdhocFee || hasNewSubcontractorAdhocFee;

      if (adhocSubcontractorId) {
        res.isUpdateSubcontractor = true;
      }

      return res;
    }
  };

  const transform = async (data: any) => {
    // Ensure isUpdateSubcontractor is a boolean
    data.isUpdateSubcontractor = !!data.isUpdateSubcontractor;

    // If the toggle is ON, keep the new subcontractor ID, otherwise remove it
    data.newSubcontractorId = data.isUpdateSubcontractor
      ? data.newSubcontractorId
      : null;

    // If updating the subcontractor and new subcontractor exists,
    // create the adhoc fee for new subcontractor
    data.newSubcontractorAdhocFee =
      data.isUpdateSubcontractor && data.newSubcontractorId
        ? {
            amount: Number(Math.round(Number(data.newPrice || 0) * 100.0)),
            category: data.newCategory,
            date: data.newDate,
            remarks: data.newRemarks,
            routeName: data.newRouteName,
            schoolId: data.newSchoolId,
            subcontractorId: data.newAdhocFeeSubcontractorId,
            typePeriod: "date",
          }
        : null;

    // If updating the subcontractor and old subcontractor exists,
    // create the adhoc fee for old subcontractor
    data.oldSubcontractorAdhocFee =
      data.isUpdateSubcontractor && data.subcontractorId
        ? {
            amount: Number(Math.round(Number(data.oldPrice || 0) * 100.0)),
            category: data.oldCategory,
            date: data.oldDate,
            remarks: data.oldRemarks,
            routeName: data.oldRouteName,
            schoolId: data.oldSchoolId,
            subcontractorId: data.oldAdhocFeeSubcontractorId,
            typePeriod: "date",
          }
        : null;

    // Remove fields not required by the API
    // This includes form-only helpers and UI-specific fields
    delete data.assignmentId;
    delete data.createdAt;
    delete data.date;
    delete data.erpCharges;
    delete data.isInvoiced;
    delete data.isLocked;
    delete data.lastSavedTotalStudent;
    delete data.lastSavedVehicleSize;
    delete data.routeName;
    delete data.schoolId;
    delete data.subcontractorRouteTransactionId;
    delete data.updatedAt;
    delete data.hasExistingAdhocFees;
    delete data.newCategory;
    delete data.newDate;
    delete data.newRemarks;
    delete data.newRouteName;
    delete data.newSchoolId;
    delete data.newAdhocFeeSubcontractorId;
    delete data.newPrice;
    delete data.oldCategory;
    delete data.oldDate;
    delete data.oldRemarks;
    delete data.oldRouteName;
    delete data.oldSchoolId;
    delete data.oldAdhocFeeSubcontractorId;
    delete data.oldPrice;

    return data;
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        isDefaultTitle
        title="Create Subcontractor Timesheet"
        transform={transform}
        actions={null}
        {...props}
      >
        <SimpleForm
          record={formatRecord(record)}
          validate={formValidate}
          toolbar={
            <CustomEditToolbar
              redirectLink={redirectAction}
              isShowDelete
              isDeleteWithConfirm
            />
          }
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">General Info</Typography>
            </Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="Subcontractor"
                source="subcontractorId"
                reference="subcontractors"
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="School"
                source="schoolId"
                reference="schools"
                validate={required()}
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Timesheet Detail</Typography>
            </Grid>

            <Grid item xs={4}>
              <CustomDatePickerInput name="date" label="Date *" disableValue />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <ReferenceInput
                source="assignmentId"
                label="Assignment"
                reference="assignments"
                validate={required()}
                disabled
              >
                <AutocompleteInput optionText="routeName" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) => (
                <>
                  <Grid item xs={4}>
                    <CustomNumberInput
                      source="totalStudent"
                      label="Total Student"
                      isRequired={!formData.isUpdateSubcontractor}
                      isDisabled={formData.isUpdateSubcontractor}
                    />
                  </Grid>
                  <Grid item xs={8}></Grid>

                  <Grid item xs={4}>
                    <SelectInput
                      label="Vehicle Size"
                      source="vehicleSize"
                      choices={vehicleSize}
                      validate={
                        formData.isUpdateSubcontractor ? undefined : required()
                      }
                      disabled={formData.isUpdateSubcontractor}
                    />
                  </Grid>
                  <Grid item xs={8}></Grid>
                </>
              )}
            </FormDataConsumer>

            <Grid item xs={4}>
              <TextInput label="Remark" source="remark" />
            </Grid>
            <Grid item xs={8}></Grid>

            <NewSubcontractorInput
              adhocSubcontractorId={adhocSubcontractorId}
            />

            <Grid item xs={12}>
              <FormDataConsumer>
                {({ formData }) => {
                  const combinedSubcontractorAdhocFees = [];

                  if (formData.oldSubcontractorAdhocFee) {
                    combinedSubcontractorAdhocFees.push({
                      ...formData.oldSubcontractorAdhocFee,
                      type: "old",
                    });
                  }

                  if (formData.newSubcontractorAdhocFee) {
                    combinedSubcontractorAdhocFees.push({
                      ...formData.newSubcontractorAdhocFee,
                      type: "new",
                    });
                  }

                  return (
                    <>
                      {combinedSubcontractorAdhocFees.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Subcontractor Adhoc Fees
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <SubcontractorTimesheetAdhocFeeTable
                          data={combinedSubcontractorAdhocFees}
                        />
                      </Grid>
                    </>
                  );
                }}
              </FormDataConsumer>
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default SubcontractorTimesheetEdit;
